/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Table } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import { Tooltip } from 'ksnm-common-ui/lib/Tooltip';
import { sortBy } from 'lodash-es';
import type { LocalDataSource } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { RenderParam } from 'ksnm-common-ui/lib/Table2/Table.interface';
import type {
  Filter as TableFilter,
  ResponseDataRow,
  ResponseDataRows,
} from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { ErrorsModal } from '@common/ErrorsModal';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { auditStore } from '@stores/auditStore';
import { constantStore } from '@stores/constantStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { periodsStore } from '@stores/dictionaryStores/periodsStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';
import type { CompanyReportDto, ReportingBlockDto, ReportingPackageDto } from '@src/api';

import { RoutePath } from '../../AppRouter/constants';
import { useSetGroupColumnWidth } from '../useSetGroupColumnWidth';
import { ActionButton } from './ActionButton';
import { AddPackageDrawer } from './AddPackageDrawer';
import { AddReportDrawer } from './AddReportDrawer';
import { fieldFormat, getColumns } from './columns';
import { DownloadReportExcelModal } from './DownloadReportExcelModal';
import { PublicationRegisterDrawer } from './PublicationRegisterDrawer';
import { RejectModal } from './RejectModal';
import { UploadReportModal } from './UploadReportModal';

import styles from './PublicationRegister.module.scss';

export type UploadParamsType = {
  uploadType: 'excel' | 'xml';
  organizationName?: string;
  reportPackageCode: string;
  reportVersionCode: string;
};

const { TABLE_NAMES } = constantStore;

const loadFromSource = (dataSource: CompanyReportDto[], topRow: ResponseDataRow = {}, tlevel = 1): ResponseDataRows => {
  const output: ResponseDataRows = [];
  for (const row of dataSource) {
    const currentRow: any = {
      ...topRow,
      ...row,
      children: undefined,
      [`name${tlevel}`]: row.name,
    };

    if (tlevel === 4) {
      currentRow.packageInfo = row;
    }

    if (row.children && row.children.length > 0) {
      loadFromSource(row.children, currentRow, tlevel + 1).forEach((subrow) => output.push(subrow));
    } else {
      output.push(currentRow);
    }
  }

  return sortBy(output, [
    'reportPackageYear',
    'reportGroupCode',
    'periodCode',
    (i) => Number(i.reportPackageCorrection ?? 0),
    'name5',
    'name6',
  ]);
};

interface IPublicationRegister {
  tabs: JSX.Element;
  filters: { [p: string]: string | undefined };
  organizationCode: string;
}

export const PublicationRegister = observer(
  ({ tabs, filters, organizationCode }: IPublicationRegister): JSX.Element => {
    const [hardFilter, setHardFilter] = useState<TableFilter[][]>([]);

    const [open, toggle] = useReducer((state) => !state, false);
    const [openAddPackageDrawer, toggleAddPackageDrawer] = useReducer((state) => !state, false);
    const [openAddReportDrawer, toggleAddReportDrawer] = useReducer((state) => !state, false);
    const [openUploadModal, toggleUploadModal] = useReducer((state) => !state, false);
    const [openRejectModal, toggleRejectModal] = useReducer((state) => !state, false);
    const [openDownloadReportExcelModal, toggleDownloadReportExcelModal] = useReducer((state) => !state, false);

    const [selectEntity, setSelectEntity] = useState<ReportingPackageDto & ReportingBlockDto>({});
    const [uploadParams, setUploadParams] = useState<UploadParamsType>();

    const { periods } = periodsStore;
    const { fetch, publicationRegister, clear, errors, toggleErrorsModal, openErrorsModal, setSelected } =
      publicationRegisterStore;

    const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.PUBLICATION_REGISTER });

    useSetGroupColumnWidth(TABLE_NAMES.PUBLICATION_REGISTER);

    useEffect(() => {
      auditStore.createAuditData({
        moduleName: 'Calculator',
        serviceName: 'Reporting_preparation',
        operationName: 'View_reporting_preparation_tree',
        message: 'Просмотр реестра подготовки отчётности',
      });
    }, []);

    useEffect(() => {
      if (organizationCode) {
        clear();
        fetch(organizationCode);
      }
    }, [clear, fetch, organizationCode]);

    useEffect(() => {
      update();
    }, [update]);

    useEffect(() => {
      const newFilter: TableFilter[] = [];

      if (filters.organization) {
        newFilter.push({ name: 'name1', type: FILTER.EQ, values: [filters.organization], filterIsInverted: false });
      }

      if (filters.year) {
        newFilter.push({ name: 'name2', type: FILTER.EQ, values: [filters.year], filterIsInverted: false });
      }

      if (filters.group) {
        newFilter.push({ name: 'name3', type: FILTER.EQ, values: [filters.group], filterIsInverted: false });
      }

      if (filters.package) {
        newFilter.push({ name: 'name4', type: FILTER.EQ, values: [filters.package], filterIsInverted: false });
      }
      setHardFilter([newFilter]);
    }, [filters]);

    useEffect(() => {
      fetchReferences(['periods']);
    }, []);

    useEffect(() => {
      if (publicationRegister.length > 0) update();
    }, [publicationRegister.length, update]);

    const localData = useMemo<LocalDataSource>(
      () => ({
        data: loadFromSource(publicationRegister),
        fieldFormat: fieldFormat,
      }),
      [publicationRegister],
    );

    useEffect(
      () => () => {
        clear();
      },
      [clear],
    );

    const onRowClick = useCallback(
      (row) => {
        if (row.level === 4) {
          const out = localData.data.find((item) => item.name4 === row.name4)?.packageInfo as ReportingPackageDto;
          setSelectEntity(out);
          toggle();
          return;
        }

        if (row.reportPackageCode) {
          setSelectEntity(row);
          toggle();
        }
      },
      [localData.data],
    );

    const emptyCellGroupRender = useCallback(({ data }: RenderParam) => {
      const setLocalData = () => localStorage.setItem('expandedTreeFromAnotherRouter', JSON.stringify({ ...data }));

      return (
        <div className={styles.report}>
          {data.name6}
          <Tooltip content="Перейти в Просмотр отчетности">
            <Link
              onContextMenu={setLocalData}
              onMouseUp={setLocalData}
              onClick={setLocalData}
              to={RoutePath.REPORTING_TREE}
            >
              <Icon name="exit" fill="#bacade" />
            </Link>
          </Tooltip>
        </div>
      );
    }, []);

    const columns = useMemo(() => getColumns(localData.data, periods), [localData.data, periods]);

    const onCheckBoxChange = useCallback(
      async (tr, sr) => {
        const rows = await getLocalRowsByCheckBoxStatus({ tableRequest: tr, localData, selectRows: sr });
        setSelected({ selectRows: sr, rows });
      },
      [localData, setSelected],
    );

    return (
      <>
        <Table
          tableName={TABLE_NAMES.PUBLICATION_REGISTER}
          tableTitle={tabs}
          externalControl={externalControl}
          resource=""
          keyField="id"
          key={key}
          columns={columns}
          hardFilter={hardFilter}
          localDataSource={localData}
          onRowClick={onRowClick}
          showAggregateRow={false}
          cellHeight={40}
          emptyCellGroupRender={emptyCellGroupRender}
          doNotShowGroupPanel
          disableChangeGroup
          onCheckBoxChange={onCheckBoxChange}
          extendTableButtons={
            <ActionButton
              toggleAddPackageDrawer={toggleAddPackageDrawer}
              toggleAddReportDrawer={toggleAddReportDrawer}
              toggleUploadModal={toggleUploadModal}
              toggleRejectModal={toggleRejectModal}
              toggleDownloadReportExcelModal={toggleDownloadReportExcelModal}
              setUploadParams={setUploadParams}
              data={localData.data}
              update={update}
            />
          }
        />

        <PublicationRegisterDrawer
          open={open}
          toggle={toggle}
          selected={selectEntity}
          toggleAddPackageDrawer={toggleAddPackageDrawer}
        />

        <AddPackageDrawer visible={openAddPackageDrawer} onClose={toggleAddPackageDrawer} />
        <AddReportDrawer visible={openAddReportDrawer} onClose={toggleAddReportDrawer} />
        <UploadReportModal
          uploadParams={uploadParams}
          toggleUploadModal={toggleUploadModal}
          openUploadModal={openUploadModal}
        />
        <RejectModal open={openRejectModal} toggle={toggleRejectModal} update={update} />
        <ErrorsModal errors={errors} open={openErrorsModal} toggle={toggleErrorsModal} />
        <DownloadReportExcelModal open={openDownloadReportExcelModal} toggle={toggleDownloadReportExcelModal} />
      </>
    );
  },
);
